// =============================================================================
// FriendsScreen — Discord-style tabbed friends view (rendered in the home area).
// =============================================================================
// Tabs: All / Pending / Blocked / Add Friend. The All tab lists online friends
// popover (which carries relationship actions) plus quick Message/Call buttons.
// Data + live updates come from useFriends().
// =============================================================================

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useFriends, useVoicePresence } from '../hooks';
import { api, ApiError } from '../api';
import { startCall } from '../calls';
import { Avatar, Button, Icon, Input, Spinner } from '../components/ui';
import { UserClickTarget } from '../components/UserPopover';
import type { FriendUser } from '../types';

type Tab = 'all' | 'pending' | 'blocked' | 'add';

const TABS: { id: Tab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'blocked', label: 'Blocked' },
  { id: 'add', label: 'Add Friend' },
];

export function FriendsScreen() {
  const { friends, incoming, outgoing, blocked, loading, refresh, incomingCount } = useFriends();
  const voicePresence = useVoicePresence();
  const [tab, setTab] = useState<Tab>('all');

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header + tabs */}
      <div className="px-4 py-2 border-b border-border bg-surface flex items-center gap-3 shrink-0 flex-wrap">
        <div className="flex items-center gap-2 font-bold text-text">
          <Icon name="user" size={15} className="text-text-muted" />
          Friends
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-sm px-2.5 py-1 rounded-aim transition-colors ${
                t.id === 'add'
                  ? tab === 'add'
                    ? 'bg-[#3BA55D] text-white font-semibold'
                    : 'text-[#3BA55D] hover:bg-elevated font-semibold'
                  : tab === t.id
                    ? 'bg-elevated text-text font-semibold'
                    : 'text-text-muted hover:bg-elevated/60'
              }`}
            >
              {t.label}
              {t.id === 'pending' && incomingCount > 0 && (
                <span className="ml-1.5 bg-danger text-white text-[10px] font-bold px-1.5 rounded-full">
                  {incomingCount}
                </span>
              )}
              {t.id === 'all' && friends.length > 0 && (
                <span className="ml-1.5 text-[10px] font-bold text-text-subtle">{friends.length}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto bg-bg p-3">
        {loading ? (
          <div className="text-center text-text-subtle text-sm py-8">
            <Spinner size={14} /> Loading…
          </div>
        ) : tab === 'add' ? (
          <AddFriendPanel onDone={refresh} />
        ) : tab === 'pending' ? (
          <PendingPanel incoming={incoming} outgoing={outgoing} onChange={refresh} />
        ) : tab === 'blocked' ? (
          <ListPanel
            users={blocked}
            empty="You haven't blocked anyone."
            renderActions={(u) => (
              <ActionButton
                title="Unblock"
                icon="check"
                onClick={async () => {
                  await api.unblockUser(u.id);
                  refresh();
                }}
              />
            )}
          />
        ) : (
          <AllFriendsPanel friends={friends} onChange={refresh} voicePresence={voicePresence} />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// All tab — online friends grouped at the top, offline below
// =============================================================================
function AllFriendsPanel({
  friends,
  onChange,
  voicePresence,
}: {
  friends: FriendUser[];
  onChange: () => void;
  voicePresence: Map<string, string>;
}) {
  const [query, setQuery] = useState('');

  if (friends.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16 gap-3">
        <div className="w-14 h-14 rounded-full bg-elevated flex items-center justify-center">
          <Icon name="user" size={26} className="text-text-subtle" />
        </div>
        <div className="text-text font-semibold">No friends yet</div>
        <div className="text-sm text-text-subtle max-w-xs">
          Head to the <span className="text-[#3BA55D] font-semibold">Add Friend</span> tab to send a request by username.
        </div>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const matches = (f: FriendUser) =>
    !q ||
    (f.displayName || '').toLowerCase().includes(q) ||
    f.username.toLowerCase().includes(q);
  const byName = (a: FriendUser, b: FriendUser) =>
    (a.displayName || a.username).localeCompare(b.displayName || b.username);
  const visible = friends.filter(matches);
  const online = visible.filter((f) => f.online).sort(byName);
  const offline = visible.filter((f) => !f.online).sort(byName);

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search friends…"
        aria-label="Search friends"
      />
      {visible.length === 0 ? (
        <div className="text-center text-text-subtle text-sm py-8">No friends match “{query.trim()}”.</div>
      ) : (
        <div className="space-y-4">
          {online.length > 0 && (
            <Section label={`Online — ${online.length}`}>
              {online.map((u) => (
                <FriendRow
                  key={u.id}
                  user={u}
                  showPresence
                  inVoice={voicePresence.has(u.id)}
                  actions={<FriendQuickActions user={u} onChange={onChange} />}
                />
              ))}
            </Section>
          )}
          {offline.length > 0 && (
            <Section label={`Offline — ${offline.length}`}>
              {offline.map((u) => (
                <FriendRow key={u.id} user={u} showPresence actions={<FriendQuickActions user={u} onChange={onChange} />} />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold px-2 mb-1">{label}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// =============================================================================
// Shared list + row
// =============================================================================
function ListPanel({
  users,
  empty,
  showPresence,
  renderActions,
}: {
  users: FriendUser[];
  empty: string;
  showPresence?: boolean;
  renderActions: (u: FriendUser) => ReactNode;
}) {
  if (users.length === 0) {
    return <div className="text-center text-text-subtle text-sm py-8">{empty}</div>;
  }
  return (
    <div className="space-y-0.5 max-w-2xl mx-auto">
      {users.map((u) => (
        <FriendRow key={u.id} user={u} showPresence={showPresence} actions={renderActions(u)} />
      ))}
    </div>
  );
}

function FriendRow({
  user,
  showPresence,
  inVoice,
  actions,
}: {
  user: FriendUser;
  showPresence?: boolean;
  inVoice?: boolean;
  actions: ReactNode;
}) {
  const name = user.displayName || user.username;
  return (
    <div className="flex items-center gap-3 px-2 py-2 rounded-aim hover:bg-elevated/50 group">
      <UserClickTarget
        userId={user.id}
        username={user.username}
        displayName={user.displayName}
        avatarUrl={user.avatarUrl}
        className="flex items-center gap-3 min-w-0 flex-1"
      >
        <div className="relative shrink-0">
          <Avatar name={name} url={user.avatarUrl} size={32} />
          {showPresence && (
            <span
              className={`absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full border-2 border-bg ${
                inVoice
                  ? 'w-[15px] h-[15px] bg-online text-white'
                  : `w-3 h-3 ${user.online ? 'bg-online' : 'bg-text-subtle'}`
              }`}
            >
              {inVoice && <Icon name="speaker" size={8} />}
            </span>
          )}
        </div>
        <div className="min-w-0 text-left">
          <div className="text-sm font-semibold text-text truncate">{name}</div>
          {inVoice ? (
            <div className="text-[11px] text-online font-medium truncate flex items-center gap-1">
              <Icon name="speaker" size={10} /> In a voice call
            </div>
          ) : (
            <div className="text-[11px] text-text-subtle truncate">@{user.username}</div>
          )}
        </div>
      </UserClickTarget>
      <div className="flex items-center gap-1.5 shrink-0">{actions}</div>
    </div>
  );
}

function ActionButton({
  title,
  icon,
  onClick,
  danger,
}: {
  title: string;
  icon: string;
  onClick: () => void | Promise<void>;
  danger?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      title={title} aria-label={title}
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await onClick();
        } finally {
          setBusy(false);
        }
      }}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-aim bg-surface hover:bg-panel transition-colors disabled:opacity-50 ${
        danger ? 'text-danger' : 'text-text-muted hover:text-text'
      }`}
    >
      {busy ? <Spinner size={12} /> : <Icon name={icon} size={15} />}
    </button>
  );
}

function FriendQuickActions({ user, onChange }: { user: FriendUser; onChange: () => void }) {
  const navigate = useNavigate();
  const name = user.displayName || user.username;

  async function message() {
    const thread = await api.openDm({ userId: user.id });
    navigate(`/dm/${thread.channelId}`);
  }
  async function call() {
    const thread = await api.openDm({ userId: user.id });
    navigate(`/dm/${thread.channelId}`);
    startCall(thread.channelId, name);
  }

  return (
    <>
      <ActionButton title={`Message ${name}`} icon="send" onClick={message} />
      <ActionButton title={`Call ${name}`} icon="phone" onClick={call} />
      <ActionButton
        title="Remove friend"
        icon="trash"
        danger
        onClick={async () => {
          await api.unfriend(user.id);
          onChange();
        }}
      />
    </>
  );
}

// =============================================================================
// Pending tab — incoming (accept/decline) + outgoing (cancel)
// =============================================================================
function PendingPanel({
  incoming,
  outgoing,
  onChange,
}: {
  incoming: FriendUser[];
  outgoing: FriendUser[];
  onChange: () => void;
}) {
  if (incoming.length === 0 && outgoing.length === 0) {
    return <div className="text-center text-text-subtle text-sm py-8">No pending requests.</div>;
  }
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {incoming.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold px-2 mb-1">
            Incoming — {incoming.length}
          </div>
          <div className="space-y-0.5">
            {incoming.map((u) => (
              <FriendRow
                key={u.id}
                user={u}
                actions={
                  <>
                    <ActionButton
                      title="Accept"
                      icon="check"
                      onClick={async () => {
                        await api.acceptFriend(u.id);
                        onChange();
                      }}
                    />
                    <ActionButton
                      title="Decline"
                      icon="trash"
                      danger
                      onClick={async () => {
                        await api.removeFriendRequest(u.id);
                        onChange();
                      }}
                    />
                  </>
                }
              />
            ))}
          </div>
        </div>
      )}
      {outgoing.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-text-subtle font-bold px-2 mb-1">
            Outgoing — {outgoing.length}
          </div>
          <div className="space-y-0.5">
            {outgoing.map((u) => (
              <FriendRow
                key={u.id}
                user={u}
                actions={
                  <ActionButton
                    title="Cancel request"
                    icon="trash"
                    danger
                    onClick={async () => {
                      await api.removeFriendRequest(u.id);
                      onChange();
                    }}
                  />
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Add Friend tab — send a request by username
// =============================================================================
function AddFriendPanel({ onDone }: { onDone: () => void }) {
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  async function submit() {
    const u = username.trim();
    if (!u) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.addFriend({ username: u });
      setResult({
        ok: true,
        msg: r.status === 'accepted' ? `You're now friends with @${u}!` : `Friend request sent to @${u}.`,
      });
      setUsername('');
      onDone();
    } catch (err) {
      setResult({ ok: false, msg: err instanceof ApiError ? err.message : 'Failed to send request' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="font-bold text-text mb-1">Add a friend</div>
      <div className="text-xs text-text-muted mb-3">Enter a username to send a friend request.</div>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <Input
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="username"
          />
        </div>
        <Button variant="primary" onClick={submit} disabled={busy || !username.trim()} className="h-[38px]">
          {busy ? <Spinner size={12} /> : <Icon name="plus" size={14} />}
          Send
        </Button>
      </div>
      {result && (
        <div className={`text-sm mt-2 ${result.ok ? 'text-[#3BA55D]' : 'text-danger'}`}>{result.msg}</div>
      )}
    </div>
  );
}
